import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { useEnvConfigModelStore } from '@/models/env-config/envConfig';
import type { RequestDocsSiteExportDataType } from '@/services/docs-site-export/requestDocsSiteExport';
import { requestDocsSiteExport } from '@/services/docs-site-export/requestDocsSiteExport';
import { DOCS_SITE_EVENT } from '@/shared/constant/Analytics';
import { DocsSiteVisitType } from '@/shared/constant/DocsIdTypeData';
import { OnlineType } from '@/shared/constant/OnlineType';
import { gaReport } from '@/shared/util/analytics';
import { commonFileHandle } from '@/shared/util/export';
import type { FormInstance } from 'aui';
import { Form, Radio } from 'aui';
import clsx from 'clsx';
import { useState } from 'react';
import { ExportFormatSelectGroup } from './ExportFormatSelectGroup';
import styles from './ExportModal.module.css';

export type TProps = {
  form?: FormInstance<any>;
};

export enum OpenApiVersion {
  'openapi2' = 'openapi2',
  'openapi30' = 'openapi30',
  'openapi31' = 'openapi31',
}

export const UrlQueryOpenApiVersion = {
  [OpenApiVersion.openapi2]: '2.0',
  [OpenApiVersion.openapi30]: '3.0',
  [OpenApiVersion.openapi31]: '3.1',
};

const Content = (props: TProps) => {
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);
  const title = useDocsDataModelStore((x) => x.docsBaseConfig.title);
  const isHideAppFormatExportOption = useDocsDataModelStore(
    (x) => x.docsBaseConfig.hideAppFormatExportOption,
  );
  const { onlineId, onlineType } = useDocsDataModelStore((x) => x.docsIdTypeData);
  const projectId = useDocsDataModelStore((x) => x.docsIdTypeData.projectId);
  const branchId = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return undefined;
    }
    return x.docsIdTypeData.branchId;
  });
  const subdirectory = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return '';
    }
    return x.docsIdTypeData.subdirectory;
  });
  const visitType = useDocsDataModelStore((x) => {
    if (x.docsIdTypeData.onlineType === OnlineType.SharedDoc) {
      return DocsSiteVisitType.WithId;
    }
    return x.docsIdTypeData.visitType;
  });
  const envConfig = useEnvConfigModelStore((x) => x);
  const appLowerName = useEnvConfigModelStore((x) => x.APP_NAME_LOWER);
  const appName = useEnvConfigModelStore((x) => x.APP_NAME);

  const [exportType, setExportType] = useState(appLowerName);
  const [openapiVersion, setOpenapiVersion] = useState<OpenApiVersion>(OpenApiVersion.openapi30);
  const [includeExtension, setIncludeExtension] = useState<boolean>(false);
  const [excludeTagsWithFolder, setExcludeTagsWithFolder] = useState(false);

  const exportRequest = async (requestData: RequestDocsSiteExportDataType) => {
    const [, response] = await requestDocsSiteExport(
      requestData,
      onlineType,
      envConfig,
      clientI18n,
      visitType,
      subdirectory,
    );
    if (response) {
      commonFileHandle(
        response,
        clientI18n('exportData.appDocumentation.json' as any, { appNameLower: appLowerName }),
        clientI18n('message.exportSuccess' as any),
      );
    }
  };

  const onSubmit = (value: { type: string }) => {
    if (!onlineId) {
      return;
    }
    gaReport(DOCS_SITE_EVENT.BUTTON_CLICK_EXPORT_DATA, { page_title: title });
    const params: {
      type: string;
      id: string;
      projectId?: string | number;
      version?: string;
      excludeExtension?: boolean;
      excludeTagsWithFolder?: boolean;
      branchId?: number;
    } = {
      type: value.type,
      id: onlineId.toString(),
    };
    if (value.type !== appLowerName) {
      params.version =
        value.type === 'openapi'
          ? UrlQueryOpenApiVersion[openapiVersion]
          : OpenApiVersion.openapi30;
    }

    if (value.type === 'openapi') {
      params.excludeExtension = !includeExtension;
      params.excludeTagsWithFolder = excludeTagsWithFolder;
    }

    switch (onlineType) {
      case OnlineType.DocsSite:
        params.branchId = branchId;
        params.projectId = projectId;
        break;
      default:
        params.projectId = projectId;
        break;
    }

    exportRequest(params);
  };

  return (
    <Form
      form={props.form}
      initialValues={{ type: appLowerName, version: openapiVersion }}
      onFinish={onSubmit}
      layout="vertical"
    >
      <Form.Item name="type" label={clientI18n('onlineShare.export.dataType')}>
        <ExportFormatSelectGroup
          value={exportType}
          onChange={(type) => setExportType(type)}
          options={[
            {
              label: 'OpenAPI Spec',
              value: 'openapi',
              icon: 'openapi',
            },
            {
              label: 'HTML',
              value: 'html',
              icon: 'html',
            },
            {
              label: 'Markdown',
              value: 'markdown',
              icon: 'markdown',
            },
            {
              label: appName,
              value: appLowerName,
              icon: appLowerName,
              isHide: isHideAppFormatExportOption,
            },
          ]}
        />
      </Form.Item>
      {exportType === 'openapi' && (
        <Form.Item name="version" label={false} style={{ position: 'relative', top: -16 }}>
          <div className={styles.subPanel}>
            <div className={styles.subTitle}>{clientI18n('exportData.export.openapi.version')}</div>
            <Radio.Group
              value={openapiVersion}
              onChange={(e) => setOpenapiVersion(e.target.value ?? OpenApiVersion.openapi30)}
            >
              <Radio value={OpenApiVersion.openapi31}>
                {clientI18n('exportData.export.openapi.version.31')}
              </Radio>
              <Radio value={OpenApiVersion.openapi30}>
                {clientI18n('exportData.export.openapi.version.30')}
              </Radio>
              <Radio value={OpenApiVersion.openapi2}>
                {clientI18n('exportData.export.openapi.version.20')}
              </Radio>
            </Radio.Group>
            <div className={clsx(styles.subTitle, 'mt-2')}>
              {clientI18n('exportData.export.openapi.includeExtension', {
                appName,
                appNameLower: appLowerName,
              })}
            </div>
            <Radio.Group
              value={includeExtension}
              onChange={(e) => setIncludeExtension(e.target.value ?? false)}
            >
              <Radio value={false}>
                {clientI18n('exportData.export.openapi.includeExtension.no')}
              </Radio>
              <Radio value={true}>
                {clientI18n('exportData.export.openapi.includeExtension.yes')}
              </Radio>
            </Radio.Group>
            <div className={clsx(styles.subTitle, 'mt-2')}>
              {clientI18n('exportData.export.openapi.tags')}
            </div>
            <Radio.Group
              value={excludeTagsWithFolder}
              onChange={(e) => setExcludeTagsWithFolder(e.target.value)}
            >
              <Radio value={false}>{clientI18n('exportData.export.openapi.tags.yes')}</Radio>
              <Radio value={true}>{clientI18n('exportData.export.openapi.tags.no')}</Radio>
            </Radio.Group>
          </div>
        </Form.Item>
      )}
    </Form>
  );
};

export default Content;
